import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../constants/api';

const AuthContext = createContext(null);

function isTokenExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true; // malformed token — treat as expired
  }
}

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [token,   setToken]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = localStorage.getItem('vistaar_token');
    const u = localStorage.getItem('vistaar_user');
    if (t && u && !isTokenExpired(t)) {
      setToken(t);
      setUser(JSON.parse(u));
    } else if (t || u) {
      localStorage.removeItem('vistaar_token');
      localStorage.removeItem('vistaar_user');
    }
    setLoading(false);
  }, []);

  function login(token, user) {
    localStorage.setItem('vistaar_token', token);
    localStorage.setItem('vistaar_user', JSON.stringify(user));
    setToken(token);
    setUser(user);
  }

  const logout = useCallback(() => {
    localStorage.removeItem('vistaar_token');
    localStorage.removeItem('vistaar_user');
    setToken(null);
    setUser(null);
  }, []);

  // Re-sync the live role/permissions/states from the server so a Super Admin's
  // changes take effect without forcing a re-login. Only admins/superadmins can
  // call /me; brokers keep their cached identity. A 403 means access was
  // revoked — log the user out.
  //
  // IMPORTANT: this callback must be STABLE (empty deps). It reads the cached
  // user from localStorage rather than closing over `user`, and only calls
  // setUser when something actually changed. If it depended on `user`, every
  // refresh would mint a new identity → new callback → any effect that runs it
  // would loop forever.
  const refreshUser = useCallback(async () => {
    const t = localStorage.getItem('vistaar_token');
    const cached = JSON.parse(localStorage.getItem('vistaar_user') || 'null');
    if (!t || !cached || cached.role === 'broker') return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/me`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${t}` },
      });
      if (res.status === 403 || res.status === 401) { logout(); return; }
      if (!res.ok) return;
      const fresh = await res.json();
      const merged = { ...cached, ...fresh };
      // No-op if nothing changed, so we never trigger a needless re-render.
      if (JSON.stringify(merged) === JSON.stringify(cached)) return;
      localStorage.setItem('vistaar_user', JSON.stringify(merged));
      setUser(merged);
    } catch { /* offline — keep cached */ }
  }, [logout]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
